import type { ReactNode } from 'react'

interface FormFieldProps {
  label?: string
  error?: string
  helperText?: string
  required?: boolean
  htmlFor?: string
  children: ReactNode
}

/**
 * エラーメッセージ表示コンポーネント
 */
export const ErrorMessage = ({ error, className = '' }: { error?: string; className?: string }) => {
  if (!error) return null
  return <span className={`text-sm text-red-600 font-light ${className}`}>{error}</span>
}

/**
 * ヘルパーテキスト表示コンポーネント
 */
export const HelperText = ({ text, className = '' }: { text?: string; className?: string }) => {
  if (!text) return null
  return <span className={`text-sm text-neutral-500 font-light ${className}`}>{text}</span>
}

/**
 * フォームフィールドの共通ラッパーコンポーネント
 * ラベル、エラーメッセージ、ヘルパーテキストを統一的に表示
 */
export const FormField = ({ label, error, helperText, required, htmlFor, children }: FormFieldProps) => {
  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label className="text-base font-light text-neutral-900 tracking-wide" htmlFor={htmlFor}>
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}

      {children}

      <ErrorMessage error={error} />
      {!error && <HelperText text={helperText} />}
    </div>
  )
}

/**
 * 入力フィールド共通のスタイル
 */
export const inputBaseStyles = (error?: string, className: string = '') => `
  px-5 py-4 border-2 rounded-lg text-base bg-white
  focus:outline-none focus:ring-2 focus:border-transparent
  disabled:bg-neutral-100 disabled:cursor-not-allowed
  transition-all duration-200
  ${error
    ? 'border-red-600 bg-red-50 focus:ring-red-600'
    : 'border-neutral-300 hover:border-neutral-400 focus:ring-primary'}
  ${className}
`
